import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { useSettings } from "../lib/settings";

export const NAV_LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/qui-sommes-nous", label: "Découvrir" },
  { to: "/agenda", label: "Nos rencontres" },
  { to: "/predications", label: "Messages" },
  { to: "/recaps", label: "Recap'" },
  { to: "/priere", label: "Prière" },
  { to: "/donner", label: "Donner" },
  { to: "/contact", label: "Contact" },
];

export function Wordmark({ dark = false }) {
  return (
    <Link to="/" data-testid="wordmark-home-link" className="group inline-flex items-center">
      {dark ? (
        <span className="inline-flex rounded-xl bg-cream px-3 py-1.5">
          <img src="/logo.png" alt="La Maison de la Charité" className="h-10 w-auto" />
        </span>
      ) : (
        <img src="/logo.png" alt="La Maison de la Charité" className="h-11 w-auto sm:h-12" />
      )}
    </Link>
  );
}

export default function Navbar() {
  const { settings } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "border-b border-line bg-cream/90 backdrop-blur-xl" : "border-b border-transparent bg-cream/60 backdrop-blur-md"
        }`}
      >
        <div className="container-site flex h-16 items-center justify-between gap-4 sm:h-[72px]">
          <Wordmark />
          <nav className="hidden items-center gap-5 lg:flex" aria-label="Navigation principale">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-semibold transition-colors duration-200 hover:text-terracotta ${
                    isActive ? "text-terracotta" : "text-stone-600"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/premiere-visite" data-testid="nav-cta-visit" className="btn-primary hidden !px-5 !py-2.5 md:inline-flex">
              {settings.cta_text}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(true)}
              aria-label="Ouvrir le menu"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white text-obsidian lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-cream lg:hidden"
          >
            <div className="container-site flex h-16 items-center justify-between">
              <Wordmark />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Fermer le menu"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white text-obsidian"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="container-site flex flex-1 flex-col justify-center gap-1 pb-24" aria-label="Navigation mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `block border-b border-line py-4 font-serif text-3xl tracking-tight ${isActive ? "italic text-terracotta" : "text-obsidian"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="pt-8"
              >
                <Link to="/premiere-visite" data-testid="mobile-nav-cta-visit" className="btn-primary w-full">
                  {settings.cta_text}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
